import { Box, Paper, Typography, Stack, useTheme, Divider, Fade, Skeleton, IconButton } from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { FiDownload, FiCheckCircle, FiLayers, FiInfo, FiMonitor, FiDatabase, FiXCircle } from "react-icons/fi";
import ColumnVisibilityButton from "../../components/button/ColumnVisibilityButton";
import RefreshButton from "../../components/button/RefreshButton";
import SaveButton from "../../components/button/SaveButton";
import ResizableBox from "../../components/container/ResizableBox";
import { TableHeaderContainer } from "../../components/container/TableHeaderContainer";
import { TableHeaderToolsContainer } from "../../components/container/TableHeaderToolsContainer";
import { TableTitleContainer } from "../../components/container/TableTitleContainer";
import ColumnMenu from "../../components/context-menu/ColumnMenu";
import ContextMenu from "../../components/context-menu/ContextMenu";
import ExportAsXLSXButton from "../../components/export/ExportAsXLSXButton";
import AgGrid from "../../components/grid/AgGrid";
import { centerAlignColDef, defaultColDef, floatFormater, leftAlignColDef, rightAlignColDef } from "../../components/grid/columnDefinitions/columnDefinitions";
import GridSelectEditor from "../../components/grid/GridSelectEditor";
import SearchBox from "../../components/input/SearchBox";
import { useContextProvider } from "../../components/page-context/ContextProvider";
import SegmentedTabs from "../../components/tabs/SegmentedTabs";
import { getOrgTokens } from "../../theme/style/organizationPalette";
import { EnvFactor } from "../../db_access/env_factor";
import { PendingValidation } from "../../db_access/pending_validation";
import { Process } from "../../db_access/process";
import { Product } from "../../db_access/product";
import { Project } from "../../db_access/project";
import { Building } from "../../db_access/building";
import { Structure } from "../../db_access/structure";
import { Supply_chain } from "../../db_access/supply_chain";
import { ReusedProduct } from "../../db_access/reused_product";
import Footprint from "../../environment-page-components/Footprint";
import LifeCycleAssessment from "../../environment-page-components/LifeCycleAssessment";
import LifeCycleInventory from "../../environment-page-components/LifeCycleInventory";
import ProductInfo from "../../environment-page-components/ProductInfo";
import ProjectInfo from "../../environment-page-components/ProjectInfo";
import BuildingInfoTab from "../Building/tabs/BuildingInfoTab";
import WBLCATab from "../Building/tabs/WBLCATab";
import ResultTab from "../Building/tabs/ResultTab";
import BuildingScopeAndGoals from "../../environment-page-components/BuildingScopeAndGoals";
import ReuseImpactSummary from "../../environment-page-components/ReuseImpactSummary";
import SankeyDiagram from "../../environment-page-components/SankeyDiagram";
import ScopeAndGoals from "../../environment-page-components/ScopeAndGoals";
import SupplierInfo from "../../environment-page-components/SupplierInfo";
import SystemBoundary from "../../environment-page-components/SystemBoundary";
import ReusedProductProductInfo from "../Products/Reuse_Workspace/ReusedProduct_ProductInfo";
import ReusedProductScopeAndGoals from "../Products/Reuse_Workspace/ReusedProduct_ScopeAndGoals";
import ReusedProductSupplierInfo from "../Products/Reuse_Workspace/ReusedProduct_SupplierInfo";

const ValidationGrid = ({ type }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const tok = getOrgTokens(isDark);
  const { 
    refresh, setRefresh, columnVisibilities, isSaveVisible, setIsSaveVisible, isSaveClicked, 
    setIsSaveClicked, setIsLoading, setSelectedProductIds, setSelectedProjectIds, 
    setSelectedBuildingIds, setSelectedBuildings, enqueueAlert 
  } = useContextProvider();
  
  const gridRef = useRef(null);
  const gridHeaderContainerRef = useRef(null);
  const LifeCycleAssessmentRef = useRef(null);
  const colVisibility_key = "validation_dashboard_table_column_visibility";
  
  const not_validated = useMemo(() => ({ label: "Not Validated", value: "Not Validated" }), []);
  const pending = useMemo(() => ({ label: "Pending", value: "Pending" }), []);
  const validated = useMemo(() => ({ label: "Validated", value: "Validated" }), []);
  const validationStatus = useMemo(() => [not_validated, pending, validated], [not_validated, pending, validated]);
  
  const info_tab = useMemo(() => ({ label: "Information", value: "Info" }), []);
  const lca_tab = useMemo(() => ({ label: "Assessments", value: "LCA" }), []);
  const result_tab = useMemo(() => ({ label: "Results", value: "Result" }), []);
  
  const virgin = useMemo(() => ({ label: "Virgin", value: "Virgin" }), []);
  const reused = useMemo(() => ({ label: "Reused", value: "Reused" }), []);
  const subTabs = useMemo(() => [virgin, reused], [virgin, reused]);
  
  const [templates, setTemplates] = useState([]);
  const [selectedTemplates, setSelectedTemplates] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [columnMenuVisible, setColumnMenuVisible] = useState(false);
  const [menuVisible, setMenuVisible] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
  const [actionQueue, setActionQueue] = useState([]);
  const [activeTab, setActiveTab] = useState(info_tab.value);
  const [activeSubTab, setActiveSubTab] = useState(virgin.value);
  const [processes, setProcesses] = useState([]);
  const [envfactors, setEnvfactors] = useState([]);
  const [reusedProduct, setReusedProduct] = useState({ id: null });
  const [selectedBuildingSection, setSelectedBuildingSection] = useState(null);
  const [fullRecordData, setFullRecordData] = useState(null);
  const [isRecordLoading, setIsRecordLoading] = useState(false);
  
  const selectedTemplate = useMemo(() => (selectedTemplates.length === 1 ? selectedTemplates[0] : null), [selectedTemplates]);
  
  const tabs = useMemo(() => {
    if (!selectedTemplate) return [];
    if (selectedTemplate.record_type === 'organization' || selectedTemplate.record_type === 'structure' || selectedTemplate.record_type === 'supply_chain') {
      return [info_tab];
    }
    return [info_tab, lca_tab, result_tab];
  }, [selectedTemplate, info_tab, lca_tab, result_tab]);

  const colDefs = useMemo(() => {
    const isHidden = (field) => !columnVisibilities[colVisibility_key]?.includes(field);
    const onCellValueChanged = (params) => {
      const { data } = params;
      const updatedRow = data;
      setTemplates((prev) => prev.map((row) => (row.id === updatedRow.id ? updatedRow : row)));
      setActionQueue((q) => [...q, { action: "set_validation_status", item: updatedRow }]);
      setIsSaveVisible(true);
    };
    return [
      { headerName: "Name", field: "name", cellStyle: { fontWeight: 500, ...leftAlignColDef.cellStyle }, ...defaultColDef, headerCheckboxSelection: true },
      { headerName: "Company", field: "company_name", ...defaultColDef, ...leftAlignColDef },
      { headerName: "User", field: "user_name", ...defaultColDef, ...centerAlignColDef },
      !type && { headerName: "Record Type", field: "record_type", ...defaultColDef, ...leftAlignColDef },
      type === 'organization' && { headerName: "Category", field: "category_label", ...defaultColDef, ...centerAlignColDef },
      { headerName: "Year", field: "year", ...defaultColDef, ...centerAlignColDef },
      { headerName: "GWP (kg CO₂e)", field: "gwp", ...rightAlignColDef, ...defaultColDef, ...floatFormater },
      { headerName: "Biogenic GWP (kg CO₂e)", field: "biogenic_gwp", ...rightAlignColDef, ...defaultColDef, ...floatFormater },
      {
        headerName: "Validation Status",
        field: "validation_status",
        ...defaultColDef,
        ...leftAlignColDef,
        cellEditor: GridSelectEditor,
        editable: true,
        onCellValueChanged,
        cellEditorParams: {
          values: validationStatus,
          autoOpen: true,
        },
      },
      { headerName: "Date Modified", field: "date_modified", ...defaultColDef, ...leftAlignColDef },
    ]
      .filter(Boolean)
      .map((colDef) => ({ ...colDef, hide: isHidden(colDef.field) }));
  }, [columnVisibilities, colVisibility_key, type, validationStatus, setIsSaveVisible]);

  const defaultCols = useMemo(() => colDefs.map((col) => col.field), [colDefs]);

  const displayedTemplates = useMemo(() => {
    if (type) {
      return templates.filter((template) => 
        template.record_type === type || 
        (type === 'organization' && (template.record_type === 'structure' || template.record_type === 'supply_chain'))
      );
    }
    return templates;
  }, [templates, type]);

  useEffect(() => {
    // Smart Refresh: If there are unsaved changes in the grid (actionQueue), 
    // skip the global refresh to avoid overwriting pending edits.
    if (isSaveVisible) return;

    setIsLoading(true);
    PendingValidation.get_all()
      .then((res) => res.json())
      .then((data) => setTemplates(data))
      .finally(() => setIsLoading(false));
  }, [refresh, isSaveVisible]);

  useEffect(() => {
    if (gridRef.current && gridRef.current.api) {
      gridRef.current.api.setGridOption("quickFilterText", searchText);
    }
  }, [searchText]);

  const onSelectionChanged = useCallback(
    (event) => {
      const selected = event.api.getSelectedRows();
      setSelectedTemplates(selected);
      
      // Update contextual IDs for sub-components
      setSelectedProductIds(selected.filter((t) => t.record_type === Product.value).map((t) => t.product_id));
      setSelectedProjectIds(selected.filter((t) => t.record_type === Project.value).map((t) => t.project_id));
      setSelectedBuildingIds(selected.filter((t) => t.record_type === Building.value).map((t) => t.building_id || t.id));
    },
    [setSelectedTemplates, setSelectedProductIds, setSelectedProjectIds, setSelectedBuildingIds]
  );

  // FETCH FULL DATA WHEN TEMPLATE SELECTED
  useEffect(() => {
    if (!selectedTemplate) {
      setFullRecordData(null);
      setSelectedBuildings([]);
      return;
    }

    // Smart Refresh: If we are currently editing the record (isSaveVisible), 
    // we don't want to re-fetch and lose the current UI state.
    if (isSaveVisible) return;

    const id = selectedTemplate.product_id || selectedTemplate.project_id || selectedTemplate.building_id || selectedTemplate.id;
    setIsRecordLoading(true);

    let promise;
    if (selectedTemplate.record_type === Product.value) promise = Product.get_by_id(id);
    else if (selectedTemplate.record_type === Project.value) promise = Project.get_by_id(id);
    else if (selectedTemplate.record_type === Building.value) promise = Building.get_by_id(id);
    else {
      setFullRecordData(selectedTemplate);
      setIsRecordLoading(false);
      return;
    }

    if (promise) {
      promise
        .then(res => res.json())
        .then(data => {
          const record = data[0] || data;
          setFullRecordData(record);
          if (selectedTemplate.record_type === Building.value) {
            setSelectedBuildings([record]);
          }
        })
        .finally(() => setIsRecordLoading(false));
    }
  }, [selectedTemplate, refresh, isSaveVisible, setSelectedBuildings]);

  useEffect(() => {
    const save = async () => {
      for (const a of actionQueue) {
        if (a.action === "set_validation_status") {
          await PendingValidation.set_validation_status(a.item);
        }
      }
      setIsLoading(false);
      setIsSaveClicked(false);
      setIsSaveVisible(false);
      setActionQueue([]);
      setRefresh((r) => !r);
      enqueueAlert({ message: t("Validation status updated successfully"), severity: "success" });
    };
    if (isSaveClicked && actionQueue.length > 0) save();
  }, [isSaveClicked, actionQueue, setIsLoading, setIsSaveClicked, setIsSaveVisible, setRefresh, enqueueAlert, t]);

  const onRetrieve = useCallback(async () => {
    if (selectedTemplates.length === 0) return;
    setIsLoading(true);
    try {
      for (const t of selectedTemplates) {
        const id = t.product_id || t.project_id || t.building_id || t.organization_id || t.id;
        if (t.record_type === Product.value) await Product.retrieve([id]);
        else if (t.record_type === Project.value) await Project.retrieve([id]);
        else if (t.record_type === Building.value) await Building.retrieve([id]);
        else if (t.record_type === 'organization' || t.record_type === 'structure' || t.record_type === 'supply_chain') {
          if (t.sub_type === 'supply_chain' || t.category_label === 'Supply Chain') {
            await Supply_chain.retrieve([id]);
          } else {
            await Structure.retrieve([id]);
          }
        }
      }
      enqueueAlert({ message: `${selectedTemplates.length} templates retrieved successfully!`, severity: "success" });
      setRefresh(prev => !prev);
    } catch (e) {
      console.error(e);
      enqueueAlert({ message: "Error retrieving templates", severity: "error" });
    } finally {
      setIsLoading(false);
      setMenuVisible(false);
    }
  }, [selectedTemplates, setIsLoading, setRefresh, enqueueAlert]);

  const handleContextMenu = (e) => {
    e.preventDefault();
    const container = gridRef.current?.eGridDiv || gridHeaderContainerRef.current?.closest('.MuiPaper-root');
    const xPos = container ? container.getBoundingClientRect().left : e.clientX;
    setMenuPosition({ x: xPos, y: e.clientY });
    setMenuVisible(true);
  };

  const handleCloseMenu = () => setMenuVisible(false);

  useEffect(() => {
    if (!selectedTemplate) {
      setProcesses([]);
      return;
    }
    const id = selectedTemplate.product_id || selectedTemplate.project_id || selectedTemplate.building_id || selectedTemplate.id;
    if (selectedTemplate.record_type === Product.value) {
      Process.get_by_product_ids([id]).then(res => res.json()).then(data => setProcesses(data));
    } else if (selectedTemplate.record_type === Project.value) {
      Process.get_by_project_ids([id]).then(res => res.json()).then(data => setProcesses(data));
    } else if (selectedTemplate.record_type === Building.value) {
      Process.get_by_building_ids([id]).then(res => res.json()).then(data => setProcesses(data));
    }
  }, [selectedTemplate]);

  useEffect(() => {
    if (processes.length === 0) {
      setEnvfactors([]);
      return;
    }
    EnvFactor.get_by_process_ids(processes.map((p) => p.id)).then(res => res.json()).then(data => setEnvfactors(data));
  }, [processes]);

  useEffect(() => {
    if (selectedTemplate && selectedTemplate.record_type === Product.value) {
      ReusedProduct.getByProductId(selectedTemplate.product_id).then(res => res.json()).then(data => setReusedProduct(data[0] || { id: null }));
    }
  }, [selectedTemplate, refresh]);

  return (
    <Box sx={{ maxWidth: '100vw', overflowX: 'hidden' }}>
      <Paper sx={{ 
        borderRadius: tok.radius.table, 
        border: '1px solid', 
        borderColor: tok.border.table,
        bgcolor: tok.bg.card,
        overflow: 'hidden',
        boxShadow: tok.shadow.premium
      }} elevation={0}>
        <TableHeaderContainer>
          <TableTitleContainer>
            <Typography sx={{ ...tok.tableTitle }}>
              {t("Pending Validation")} ({displayedTemplates.length})
            </Typography>
          </TableTitleContainer>
          <TableHeaderToolsContainer ref={gridHeaderContainerRef}>
            <SearchBox text={searchText} setText={setSearchText} width={300} />
            <ColumnVisibilityButton onClick={() => setColumnMenuVisible((p) => !p)} />
            <ExportAsXLSXButton data={displayedTemplates} label="Export as .xlsx" filename="templates.xlsx" />
            <RefreshButton onClick={() => setRefresh(r => !r)} />
          </TableHeaderToolsContainer>
        </TableHeaderContainer>
        <Box sx={{ position: 'relative' }}>
          <ResizableBox maxHeight={600}>
            <AgGrid 
              rowData={displayedTemplates} 
              columnDefs={colDefs} 
              onSelectionChanged={onSelectionChanged} 
              handleContextMenu={handleContextMenu}
              headerCheckbox={false} 
              ref={gridRef} 
            />
          </ResizableBox>
        </Box>
      </Paper>

      {selectedTemplate && (
        <Box sx={{ mt: 5, width: '100%', animation: 'fadeIn 0.5s ease' }}>
          {/* Issue 1: Reverted Heading Design with Green Bar & Bottom Border */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            borderBottom: tok.sectionHeading.borderBottom,
            mb: tok.sectionHeading.containerMb,
            pb: tok.sectionHeading.titlePb,
            px: 2,
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              left: 0,
              top: '15%',
              bottom: '15%',
              width: tok.sectionHeading.barWidth,
              backgroundColor: tok.sectionHeading.barColor,
              borderRadius: '2px'
            }
          }}>
            <Typography sx={{ 
              fontSize: tok.sectionHeading.fontSize, 
              fontWeight: tok.sectionHeading.fontWeight, 
              color: tok.sectionHeading.color, 
              fontFamily: tok.sectionHeading.fontFamily,
              pl: tok.sectionHeading.paddingLeft
            }}>
              {selectedTemplate.record_type === Product.value ? t("Product Validation") : 
               selectedTemplate.record_type === Project.value ? t("Project Validation") : 
               selectedTemplate.record_type === Building.value ? t("Building Validation") : t("Organization Validation")} 
              {" — "} {selectedTemplate.name}
            </Typography>
            <IconButton onClick={() => setSelectedTemplates([])} size="small" sx={{ color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)', "&:hover": { color: '#ef4444' } }}>
              <FiXCircle size={20} />
            </IconButton>
          </Box>
          
          {/* Navigation Tabs with Standardized Margin Bottom (32px) */}
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: "flex", gap: 3, px: 0.5, alignItems: "center" }}>
              <Box sx={{ flex: 1 }}>
                <SegmentedTabs options={tabs} selected={activeTab} setSelected={setActiveTab} />
              </Box>
              {selectedTemplate.record_type === Product.value && (
                <Box sx={{ flex: 0.35 }}>
                  <SegmentedTabs options={subTabs} selected={activeSubTab} setSelected={setActiveSubTab} />
                </Box>
              )}
            </Box>
          </Box>

          {/* Issue 2: Standardized & Clean Workspace Spacing - Consistent gap: 4 (32px) */}
          <Box sx={{ minHeight: '400px' }}>
            {isRecordLoading ? (
              <Box sx={{ p: 4 }}>
                <Skeleton variant="rectangular" height={200} sx={{ borderRadius: '12px', mb: 2 }} />
                <Skeleton variant="rectangular" height={100} sx={{ borderRadius: '12px' }} />
              </Box>
            ) : fullRecordData ? (
              <Fade in={!isRecordLoading}>
                <Box sx={{ px: 0.5, display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {/* 1. PRODUCT AUDIT VIEW */}
                  {selectedTemplate.record_type === Product.value && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <Box sx={{ display: activeTab === info_tab.value ? "flex" : "none", flexDirection: 'column', gap: 4 }}>
                        {activeSubTab === virgin.value ? (
                          <>
                            <ProductInfo />
                            <SupplierInfo type={Product.label} selectedRow={fullRecordData} />
                          </>
                        ) : (
                          <>
                            <ReusedProductProductInfo reusedProduct={reusedProduct} />
                            <ReusedProductSupplierInfo reusedProduct={reusedProduct} />
                          </>
                        )}
                      </Box>
                      <Box sx={{ display: activeTab === lca_tab.value ? "flex" : "none", flexDirection: 'column', gap: 4 }}>
                        {activeSubTab === virgin.value ? (
                          <>
                            <ScopeAndGoals pageType={Product.value} selectedRow={fullRecordData} />
                            <LifeCycleInventory selectedID={fullRecordData.id} isSupplier={true} isReuse={false} />
                          </>
                        ) : (
                          <>
                            <ReusedProductScopeAndGoals reusedProduct={reusedProduct} />
                            <LifeCycleInventory title={"Reuse LCI"} selectedID={fullRecordData.id} isSupplier={true} isReuse={true} reusedID={reusedProduct.id} />
                          </>
                        )}
                      </Box>
                      <Box sx={{ display: activeTab === result_tab.value ? "flex" : "none", flexDirection: 'column', gap: 4 }}>
                        <Box sx={{ display: fullRecordData.is_epd !== 1 ? "flex" : "none", flexDirection: 'column', gap: 4 }}>
                          <Footprint processData={processes} pageType="supplier" />
                          <SankeyDiagram processData={processes} envfactors={envfactors} />
                        </Box>
                        {/* System Boundary precisely positioned below Sankey */}
                        <SystemBoundary processData={processes} isEpd={fullRecordData.is_epd} />
                        <LifeCycleAssessment ref={LifeCycleAssessmentRef} selectedRow={fullRecordData} LCA_type={Product.label} />
                      </Box>
                    </Box>
                  )}

                  {/* 2. PROJECT AUDIT VIEW */}
                  {selectedTemplate.record_type === Project.value && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <Box sx={{ display: activeTab === info_tab.value ? "flex" : "none", flexDirection: 'column', gap: 4 }}>
                        <ProjectInfo />
                        <SupplierInfo type={Project.label} selectedRow={fullRecordData} />
                      </Box>
                      <Box sx={{ display: activeTab === lca_tab.value ? "flex" : "none", flexDirection: 'column', gap: 4 }}>
                        <ScopeAndGoals pageType={Project.value} selectedRow={fullRecordData} />
                        <LifeCycleInventory title="Assessment Details" selectedID={fullRecordData.id} isSupplier={false} isReuse={false} />
                      </Box>
                      <Box sx={{ display: activeTab === result_tab.value ? "flex" : "none", flexDirection: 'column', gap: 4 }}>
                        <Footprint processData={processes} pageType="utilizer" />
                        <SankeyDiagram processData={processes} />
                        <SystemBoundary processData={processes} />
                        <LifeCycleAssessment selectedRow={fullRecordData} LCA_type={Project.label} />
                      </Box>
                    </Box>
                  )}

                  {/* 3. BUILDING AUDIT VIEW */}
                  {selectedTemplate.record_type === Building.value && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <Box sx={{ display: activeTab === info_tab.value ? "flex" : "none", flexDirection: 'column', gap: 4 }}>
                        <BuildingInfoTab building={fullRecordData} />
                      </Box>
                      <Box sx={{ display: activeTab === lca_tab.value ? "flex" : "none", flexDirection: 'column', gap: 4 }}>
                        <WBLCATab 
                          building={fullRecordData} 
                          selectedBuildingSection={selectedBuildingSection} 
                          setSelectedBuildingSection={setSelectedBuildingSection} 
                        />
                      </Box>
                      <Box sx={{ display: activeTab === result_tab.value ? "flex" : "none", flexDirection: 'column', gap: 4 }}>
                        <ResultTab building={fullRecordData} />
                      </Box>
                    </Box>
                  )}

                  {/* 4. ORGANIZATION AUDIT VIEW */}
                  {(selectedTemplate.record_type === 'organization' || selectedTemplate.record_type === 'structure' || selectedTemplate.record_type === 'supply_chain') && (
                    <Paper
                      elevation={0}
                      sx={{
                        p: 3,
                        borderRadius: tok.radius.card,
                        border: '1px solid',
                        borderColor: tok.border.card,
                        bgcolor: tok.bg.card,
                      }}
                    >
                      <Typography sx={{ fontSize: '16px', fontWeight: 600, color: tok.text.primary, mb: 2 }}>
                        {t("Organization Assessment Details")}
                      </Typography>
                      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2.5 }}>
                        <Box>
                          <Typography sx={{ fontSize: '12px', color: tok.text.secondary }}>{t("Organization Name")}</Typography>
                          <Typography sx={{ fontSize: '14px', fontWeight: 600, color: tok.text.primary }}>{selectedTemplate.name || "—"}</Typography>
                        </Box>
                        <Box>
                          <Typography sx={{ fontSize: '12px', color: tok.text.secondary }}>{t("Category / Boundary")}</Typography>
                          <Typography sx={{ fontSize: '14px', fontWeight: 600, color: tok.text.primary }}>{selectedTemplate.category_label || (selectedTemplate.sub_type === 'supply_chain' ? t("Supply Chain") : t("Structure"))}</Typography>
                        </Box>
                        <Box>
                          <Typography sx={{ fontSize: '12px', color: tok.text.secondary }}>{t("Base Year")}</Typography>
                          <Typography sx={{ fontSize: '14px', fontWeight: 600, color: tok.text.primary }}>{selectedTemplate.year || "—"}</Typography>
                        </Box>
                        <Box>
                          <Typography sx={{ fontSize: '12px', color: tok.text.secondary }}>{t("Company")}</Typography>
                          <Typography sx={{ fontSize: '14px', fontWeight: 600, color: tok.text.primary }}>{selectedTemplate.company_name || "—"}</Typography>
                        </Box>
                        <Box>
                          <Typography sx={{ fontSize: '12px', color: tok.text.secondary }}>{t("Submitted By")}</Typography>
                          <Typography sx={{ fontSize: '14px', fontWeight: 600, color: tok.text.primary }}>{selectedTemplate.user_name || "—"}</Typography>
                        </Box>
                        <Box>
                          <Typography sx={{ fontSize: '12px', color: tok.text.secondary }}>{t("Total GWP (kg CO₂e)")}</Typography>
                          <Typography sx={{ fontSize: '14px', fontWeight: 600, color: tok.text.primary }}>{(Number(selectedTemplate.gwp) || 0).toLocaleString()}</Typography>
                        </Box>
                        <Box>
                          <Typography sx={{ fontSize: '12px', color: tok.text.secondary }}>{t("Current Status")}</Typography>
                          <Typography sx={{ fontSize: '14px', fontWeight: 600, color: selectedTemplate.validation_status === 'Validated' ? '#10b981' : '#f59e0b' }}>{selectedTemplate.validation_status}</Typography>
                        </Box>
                      </Box>
                      <Typography sx={{ fontSize: '13px', color: tok.text.secondary, mt: 3, pt: 2, borderTop: `1px solid ${tok.border.card}` }}>
                        {t("To validate or update this organization review, change the Validation Status dropdown in the table above to Validated or Not Validated, then click Save.")}
                      </Typography>
                    </Paper>
                  )}
                </Box>
              </Fade>
            ) : null}
          </Box>
        </Box>
      )}

      <ColumnMenu
        anchorRef={gridHeaderContainerRef}
        visible={columnMenuVisible}
        onClose={() => setColumnMenuVisible(false)}
        colDefs={colDefs}
        colVisibility_key={colVisibility_key}
        defaultCols={defaultCols}
      />
      <ContextMenu 
        x={menuPosition.x} y={menuPosition.y} 
        visible={menuVisible} 
        onClose={handleCloseMenu} 
        selectedItems={selectedTemplates} 
        options={[
          { label: "Retrieve to Workspace", action: onRetrieve, icon: <FiDownload />, needSelectedItems: true },
        ]} 
      />
      <SaveButton />
    </Box>
  );
};
export default ValidationGrid;
